import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Star, MapPin, ArrowLeft, Search, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "../i18n";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [allEvents, setAllEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [locationFilter, setLocationFilter] = useState("");
  const [detectedCountry, setDetectedCountry] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [appSettings, setAppSettings] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchEvents();
    detectUserLocation();
    axios.get(`${API}/settings/public`).then(r => setAppSettings(r.data)).catch(() => {});
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setAllEvents(res.data);
      // Extract unique locations from events
      const locs = [...new Set(res.data.map(e => e.country).filter(Boolean))].sort();
      setAvailableLocations(locs);
    } catch {
      toast.error("Failed to load events");
    }
  };

  const detectUserLocation = async () => {
    try {
      // Use a free geo-IP API to detect country
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (data.country_name) {
        setDetectedCountry(data.country_name);
        setLocationFilter(data.country_name);
      }
    } catch {
      // Silently fail - user can select manually
    }
  };

  // Filter events by location
  const events = locationFilter
    ? allEvents.filter(e =>
        e.country?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        e.town?.toLowerCase().includes(locationFilter.toLowerCase())
      )
    : allEvents;

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDate(null);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const clearLocation = () => {
    setLocationFilter("");
    setShowLocationDropdown(false);
  };

  // Check if an event falls on a specific date
  const eventOnDate = (event, day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (event.recurring && event.event_date && event.end_date) {
      return dateStr >= event.event_date && dateStr <= event.end_date;
    }
    return event.event_date === dateStr;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    return events.filter(e => eventOnDate(e, day));
  };

  // Filter events list by selected date
  const displayEvents = selectedDate
    ? events.filter(e => eventOnDate(e, selectedDate))
    : events;

  return (
    <div className="min-h-screen h-screen overflow-y-auto" style={{ background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)" }} data-testid="calendar-page">
      {/* Header */}
      <nav className="nav-bar" data-testid="calendar-nav">
        <Link to="/" className="nav-logo">
          <img src={`${API}/logo`} alt="" className="w-10 h-10 rounded-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span>{appSettings?.app_name || t("app_name")}</span>
        </Link>
        <h2 className="text-lg font-bold text-slate-700" style={{ fontFamily: "Nunito" }}>
          {t("event_calendar")}
        </h2>
        <Link to="/" className="btn-secondary text-sm py-2 px-4" data-testid="back-to-map-cal">
          <ArrowLeft className="w-4 h-4" />
          {t("back_to_map")}
        </Link>
      </nav>

      <main className="max-w-5xl mx-auto p-4 md:p-8">
        {/* Location Filter Bar */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg border border-slate-200/50 p-4 mb-6" data-testid="location-filter-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <MapPin className="w-4 h-4 text-indigo-600" />
              Showing events in:
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={locationFilter}
                onChange={(e) => { setLocationFilter(e.target.value); setShowLocationDropdown(true); }}
                onFocus={() => setShowLocationDropdown(true)}
                placeholder="Type country or city..."
                className="input-field pl-10 pr-8 py-2 text-sm"
                data-testid="location-filter-input"
              />
              {locationFilter && (
                <button onClick={clearLocation} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="clear-location-btn">
                  <X className="w-4 h-4" />
                </button>
              )}
              {showLocationDropdown && availableLocations.length > 0 && (
                <div className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 w-full max-h-48 overflow-y-auto" data-testid="location-dropdown">
                  {!locationFilter && (
                    <button
                      onClick={() => { setLocationFilter(""); setShowLocationDropdown(false); }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 text-indigo-600 font-medium"
                      data-testid="show-all-locations"
                    >
                      Show all locations
                    </button>
                  )}
                  {availableLocations
                    .filter(loc => !locationFilter || loc.toLowerCase().includes(locationFilter.toLowerCase()))
                    .map(loc => (
                      <button
                        key={loc}
                        onClick={() => { setLocationFilter(loc); setShowLocationDropdown(false); }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 transition-colors ${locationFilter === loc ? "bg-indigo-50 font-bold text-indigo-700" : "text-slate-700"}`}
                        data-testid={`location-option-${loc}`}
                      >
                        {loc}
                      </button>
                    ))}
                </div>
              )}
            </div>
            {detectedCountry && locationFilter !== detectedCountry && (
              <button
                onClick={() => { setLocationFilter(detectedCountry); setShowLocationDropdown(false); }}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold px-3 py-1.5 bg-indigo-50 rounded-lg transition-colors"
                data-testid="use-detected-location-btn"
              >
                Use my location ({detectedCountry})
              </button>
            )}
            {locationFilter && (
              <button
                onClick={clearLocation}
                className="text-xs text-slate-500 hover:text-slate-700 font-medium px-3 py-1.5 bg-slate-100 rounded-lg transition-colors"
                data-testid="show-all-events-btn"
              >
                Show all
              </button>
            )}
            <span className="text-xs text-slate-400 ml-auto">{events.length} event{events.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/50 p-6" data-testid="calendar-grid">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-xl transition-colors" data-testid="prev-month-btn">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h3 className="text-xl font-black text-slate-800" style={{ fontFamily: "Nunito" }} data-testid="calendar-month-title">
                {MONTHS[month]} {year}
              </h3>
              <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-xl transition-colors" data-testid="next-month-btn">
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((d) => (
                <div key={d} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide py-2">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                const hasFeatured = dayEvents.some(e => e.featured);
                const hasEvents = dayEvents.length > 0;
                return (
                <button
                  key={idx}
                  onClick={() => day && setSelectedDate(day)}
                  disabled={!day}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-medium transition-all relative ${
                    !day ? "invisible" :
                    selectedDate === day ? "bg-indigo-600 text-white shadow-lg" :
                    isToday(day) ? "bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300" :
                    "hover:bg-slate-100 text-slate-700"
                  }`}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day}
                  {day && hasEvents && (
                    <div className="flex gap-0.5 mt-0.5">
                      {hasFeatured && <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    </div>
                  )}
                </button>
                );
              })}
            </div>
          </div>

          {/* Events List */}
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/50 p-6" data-testid="calendar-events-list">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Nunito" }}>
              <CalendarIcon className="w-5 h-5 text-indigo-600" />
              {selectedDate ? `${MONTHS[month]} ${selectedDate}` : t("all_events")}
            </h3>

            {displayEvents.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  {selectedDate
                    ? `No events on ${MONTHS[month]} ${selectedDate}`
                    : locationFilter ? `No events found in "${locationFilter}"` : t("no_events_date")}
                </p>
                {selectedDate && (
                  <button onClick={() => setSelectedDate(null)} className="text-indigo-600 text-sm font-medium mt-2 hover:text-indigo-700" data-testid="show-all-dates-btn">
                    Show all dates
                  </button>
                )}
                {locationFilter && !selectedDate && (
                  <button onClick={clearLocation} className="text-indigo-600 text-sm font-medium mt-2 hover:text-indigo-700" data-testid="clear-filter-events-btn">
                    Show all events
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {selectedDate && (
                  <button onClick={() => setSelectedDate(null)} className="text-xs text-indigo-600 font-medium hover:text-indigo-700 mb-1" data-testid="clear-date-filter-btn">
                    Show all dates
                  </button>
                )}
                {displayEvents
                  .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
                  .map((event) => (
                  <div
                    key={event.id}
                    className={`rounded-xl p-4 transition-all ${
                      event.featured
                        ? "bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 shadow-md"
                        : "bg-slate-50 border border-slate-100"
                    }`}
                    data-testid={`cal-event-${event.id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        {event.featured && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
                        <h4 className={`font-bold text-sm ${event.featured ? "text-amber-800" : "text-slate-800"}`} style={{ fontFamily: "Nunito" }}>
                          {event.title}
                        </h4>
                      </div>
                      {event.featured && (
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-bold rounded-full">
                          {t("featured")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <MapPin className="w-3 h-3 text-indigo-500 shrink-0" />
                      <span className="text-xs text-indigo-600 font-medium">{event.address || `${event.town}, ${event.country}`}</span>
                    </div>
                    {event.event_date && (
                      <p className="text-xs text-slate-500 mb-1">
                        {event.event_date}{event.recurring && event.end_date ? ` — ${event.end_date}` : ""}
                      </p>
                    )}
                    {event.description && (
                      <p className="text-xs text-slate-600 mb-1 line-clamp-2">{event.description}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="px-1.5 py-0.5 bg-slate-200 rounded text-slate-600 font-medium">{event.event_type}</span>
                      {event.schedule && <span>{event.schedule}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
